import {useTranslation} from "react-i18next";
import {useSnack} from "../../../../../../utils/useSnack";
import React, {useEffect} from "react";
import {Box, Button, Grid, Modal, TextField, Typography} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import {LoadingButton} from "@mui/lab";

import {AuctionState, AuctionType, LotState} from "../../../../../../utils/enums";
import {LotsFilterParams, LotWithItemList} from "../../../../../../types/lot";
import {lotService} from "../../../../../../api/services/lot.service";
import {Company} from "../../../../../../types/company";
import LotsTable from "./LotsTable";

export type LotModalProps = {
    isOpen: boolean;
    onClose: () => void;
    pickLots: (lots: LotWithItemList[]) => void;
    company: Company | null;
    auctionType: AuctionType;
    auctionState: AuctionState;
}
export const LotModal = ({
                               isOpen, onClose, pickLots, company,
                               auctionType, auctionState
                           }: LotModalProps) => {

    const modalStyle = {
        position: "absolute" as "absolute",
        top: "50%",
        left: "50%",
        transform: "translate(-50%, -50%)",
        width: '100vw', // Указываем нужную ширину
        bgcolor: "background.paper",
        p: 4,
    };

    const {t, i18n} = useTranslation();
    const {snack} = useSnack();
    const [lots, setLots] = React.useState<LotWithItemList[]>([]);
    const [loading, setLoading] = React.useState(false);
    const [page, setPage] = React.useState(0);
    const [pageSize, setPageSize] = React.useState(10);
    const [totalElements, setTotalElements] = React.useState(0);
    const [searchText, setSearchText] = React.useState("");
    const [isLoading, setIsLoading] = React.useState(false);
    const [selectedRows, setSelectedRows] = React.useState<LotWithItemList[]>([]);

    const onSearch = async () => {
        getCompanyLots();
    }

    const getCompanyLots = async () => {
        setSelectedRows([])
        setLoading(true);
        try {
            const response = await lotService.getLotsWithItem(company!.id,  page, pageSize, {state: LotState.awaiting, year: null, bin: null}, i18n.language)
            if (response.status === "success" && response.data) {
                setLots(response.data.data);
                setTotalElements(response.data.totalElements);
            }
        } catch (error) {
            snack(t("snack.error.default"), "error");
        } finally {
            setLoading(false);
        }
    }

    useEffect(() => {
        getCompanyLots()
    }, [page, pageSize, isOpen])

    // Обработчик для изменения состояния выбора
    const handleCheckboxChange = (row: LotWithItemList, checked: boolean) => {
        if (checked) {
            setSelectedRows((prev) => [...prev, row]);
        } else {
            setSelectedRows((prev) => prev.filter((item) => item.id !== row.id));
        }
    };

    // Кастомный Toolbar, отображающий выбранные строки
    const CustomToolbar = () => {
        return (
            <Box sx={{padding: "4px"}}>
                {selectedRows.length > 0 && (
                    <Typography variant="body2">
                        {selectedRows.length === 1
                            ? "1 строка выбрана"
                            : `${selectedRows.length} строки выбраны`}
                    </Typography>
                )}
            </Box>
        );
    };

    return (
        <Modal open={isOpen} onClose={onClose}>
            <Box sx={modalStyle}>
                {/* header */}

                <Grid container spacing={2} justifyContent="space-between" sx={{mb: 2}}>
                    <Grid item>
                        <Typography component="h5" variant="h5" sx={{fontSize: {xs: 14, sm: 20}}}>
                            {t("lots.company_lots")}
                        </Typography>
                    </Grid>
                    <Grid item xs={2.2} sm={1.2} sx={{
                        textAlign: "right",
                    }}>
                        <Button onClick={onClose} sx={{
                            color: "black"
                        }}>
                            <CloseIcon/>
                        </Button>
                    </Grid>
                </Grid>
                {/* / header */}

                <Box>
                    {/* search */}
                    <Grid container spacing={2} xs={12} sx={{mb: 2, mt: 4}}>
                        <Grid item xs={9}>
                            <TextField
                                fullWidth color="primary" type="text" size="small"
                                name="searchText" label={t("auction.modals.product.search_by_name")}
                                value={searchText} onChange={e => setSearchText(e.target.value)}
                                onKeyPress={e => {
                                    if (e.key === "Enter") {
                                        onSearch()
                                    }
                                }}
                            />
                        </Grid>
                        <Grid item xs={3}>
                            <LoadingButton fullWidth color="primary" variant="contained"
                                           onClick={onSearch} loading={isLoading}
                            >
                                {t("auction.modals.product.search")}
                            </LoadingButton>
                        </Grid>
                    </Grid>
                    {/* / search */}
                    <Box sx={{ height: '400px', overflow: 'auto' }}>

                    <LotsTable
                         companyId={company!.id}
                         page={page}
                         pageSize={pageSize}
                         setPage={setPage}
                         setPageSize={setPageSize}
                         lotsData={lots}
                         totalCount={totalElements}
                         loading={loading}
                         expandAll={true}
                         selectedRows={(selectedLots: LotWithItemList[]) => {
                             setSelectedRows(selectedLots);
                         }}

                         noChildrenMessage={t("auction.lots.no_lots")}
                   />
                    </Box>
                    {/* Кнопки "Закрыть" и "Применить" */}
                    <Box mt={2} display="flex" justifyContent="flex-end">
                        <Button
                            variant="outlined"
                            color="secondary"
                            onClick={onClose}
                            sx={{mr: 2}}
                        >
                            {t("common.close", "Закрыть")}
                        </Button>
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={() => {
                                if (pickLots) {
                                    pickLots(selectedRows);
                                }
                            }}
                        >
                            {t("common.apply", "Применить")}
                        </Button>
                    </Box>
                </Box>
            </Box>
        </Modal>
    )
}